import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import applyPromotion from "@NA/Overrides/Store/Pages/CheckoutReviewAndSubmit/Handlers/ApplyPromotion";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, CartModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import PlaceOrderButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPlaceOrderButton";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadShippingMethods from "@NA/Handlers/ADIGetShippingMethodHandler";
import setUpdateCartLoading from "@NA/Handlers/ADIUpdateCartLoadingHandler";
import checkoutErrorMessage from "@NA/Handlers/CheckoutReviewAndSubmit/ADICheckoutReviewAndSubmitPromoErrorHandler";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import { updateCartCalculationWithNewCart } from "@NA/Utilities/CheckoutUtilities"; // P21 change
import { Draft } from "immer";
import { cloneDeep } from "lodash";
import * as React from "react";
import { FC } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { Cart } from "@NA/Overrides/Services/CartService";
import { CartSummaryMethodsResults } from "@NA/Services/ADICartSummaryService";
import loadCartSummary from "@NA/Handlers/ADIGetCartSummaryMethodHandler";
import updateCart, { ADIUpdateCartResult } from "@NA/Handlers/ADIUpdateCartHandler";

interface OwnProps extends WidgetProps {
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    cart: Cart;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const newCartState = getCurrentCartState(state);
    const shippingCartState = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const promotionsDataView = getCurrentPromotionsDataView(state);
    return {
        customCart: cartState.value,
        newCartState,
        promotionsDataView,
        shippingCartState,
        alternateShipAddress: state.ADIGlobalData.checkoutCartStore.alternateShipTo,
        shipToState: getShipToState(state, state.context.session.shipToId),
        promotionState: state.ADIGlobalData.ADIPromotionStore,
        showShippingExpediteMsg: cartState.value?.properties["showShippingExpediteMsg"] ? // P21 change
            cartState.value?.properties["showShippingExpediteMsg"] : "false", // P21 change
    };
};

const mapDispatchToProps = {
    updateCartLine,
    applyPromotion,
    checkoutErrorMessage,
    loadCart,
    setUpdateCartLoading,
    loadCartSummary,
    updateCart,
};

const enum fields {
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    cssClass = "cssClass",
    openInNewWindow = "openInNewWindow",
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SummaryStyles {
    summaryHeader?: InjectableCss;
    shippingNote?: InjectableCss;
    helpTextContainer?: InjectableCss;
    summaryContainer?: InjectableCss;
    placeOrderButtonContainer?: InjectableCss;
    codeApplied?: InjectableCss;
    codeAppliedMobile?: InjectableCss;
    codeAppliedText?: InjectableCss;
    promoErrorText?: InjectableCss;
    removedPromoText?: TypographyPresentationProps;
}

export const summaryStyles: SummaryStyles = {
    summaryHeader: {
        css: css`
            font-size: 24px;
            font-weight: bold;
            line-height: normal;
            letter-spacing: normal;
            text-align: left;
            color: ${ADIColors.darkBlue1};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 20px;
                    `,
                    css`
                        font-size: 20px;
                    `,
                    css`
                        font-size: 22px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    codeAppliedText: {
        css: css`
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("success")};
        `,
    },
    codeApplied: {
        css: css`
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("success")};
            display: flex;
            align-items: center;
            > button {
                font-size: 14px;
                font-weight: bold;
                font-stretch: normal;
                font-style: normal;
                line-height: normal;
                letter-spacing: normal;
                text-align: left;
                color: ${getColor("text.link")};
                display: inline-block;
                margin-left: 6px;
                padding: 0;
                &:hover {
                    text-decoration: underline;
                    > span {
                        color: ${getColor("text.link")};
                    }
                }
            }
        `,
    },
    codeAppliedMobile: {
        css: css`
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            color: ${getColor("success")};
            display: flex;
            align-items: center;
            margin-top: 5px;
            > button {
                font-size: 14px;
                font-weight: bold;
                font-stretch: normal;
                font-style: normal;
                line-height: normal;
                letter-spacing: normal;
                text-align: left;
                color: ${getColor("text.link")};
                display: inline-block;
                margin-left: 6px;
                padding: 0;
                &:hover {
                    text-decoration: underline;
                    > span {
                        color: ${getColor("text.link")};
                    }
                }
            }
        `,
    },
    shippingNote: {
        css: css`
            font-size: 14px !important;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal !important;
            letter-spacing: normal;
            text-align: left;
            color: ${getColor("text.main")};
            margin-bottom: 26px;
            padding-top: 12px;
            @media print {
                width:100%;
                padding-bottom: 10px;
                display:block;
                clear:both;
                text-align:right;
            }
        `,
    },
    helpTextContainer: {
        css: css`
            font-size: 14px !important;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal !important;
            letter-spacing: normal;
            text-align: left;
            color: ${getColor("text.main")};
            @media print {
                width:100%;
                padding-bottom: 10px;
                display:block;
                text-align:center;
            }
        `,
    },
    summaryContainer: {
        css: css`
            padding: 21px 25px 0 23px;
            padding-bottom: 32px;
            border-radius: 4px;
            border: solid 1px ${getColor("common.border")} !important;
            background-color: ${getColor("common.background")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                    height: auto !important;
                    padding: 21px 15px 27px 15px;
                    `,
                    css`
                    height: auto !important;
                    padding: 21px 15px 27px 15px;
                    `,
                    css`
                        height: auto !important;
                    `,
                    null,
                    null,
                ])}
            input:not(.react-datetime-picker__inputGroup__input):focus,
            select:not(.react-datetime-picker__inputGroup__input):focus,
            textarea:focus,
            .mobiusFileUpload:focus,
            .react-datetime-picker:focus {
                outline-style: none !important;
            }
            input:not(.react-datetime-picker__inputGroup__input),
            select:not(.react-datetime-picker__inputGroup__input),
            textarea,
            .mobiusFileUpload,
            .react-datetime-picker {
                border: 1px solid ${ADIColors.gray2} !important;
            }
            input:not(.react-datetime-picker__inputGroup__input):focus,
            select:not(.react-datetime-picker__inputGroup__input):focus,
            textarea:focus,
            .mobiusFileUpload:focus,
            .react-datetime-picker:focus,
            input:not(.react-datetime-picker__inputGroup__input) .focus-within,
            select:not(.react-datetime-picker__inputGroup__input) .focus-within,
            textarea .focus-within,
            .mobiusFileUpload .focus-within,
            .react-datetime-picker .focus-within {
                padding: 9.5px 15px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        margin-left: 15px;
                        &::-webkit-scrollbar {
                            width: 4px;
                            overflow-y: auto;
                        }
                        &::-webkit-scrollbar-track {
                            background: ${getColor("common.background")};
                            overflow-y: auto;
                        }
                        &::-webkit-scrollbar-thumb {
                            background-color: ${ADIColors.lightGrey};
                            border-radius: 7px;
                            overflow-y: auto;
                        }
                    `,
                    css`
                        &::-webkit-scrollbar {
                            width: 4px;
                            overflow-y: auto;
                        }
                        &::-webkit-scrollbar-track {
                            background: ${getColor("common.background")};
                            overflow-y: auto;
                        }
                        &::-webkit-scrollbar-thumb {
                            background-color: ${ADIColors.lightGrey};
                            border-radius: 7px;
                            overflow-y: auto;
                        }
                    `,
                ])}
        `,
    },
    placeOrderButtonContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 26px;
            margin-bottom: 28px;
            > button {
                padding: 10px !important;
                border-radius: 4px;
                background-color: ${getColor("text.link")} !important;
                min-height: 42px;
                line-height: normal;
                max-width: 227.3px !important;
                width: 100% !important;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                            max-width: 100% !important;
                        `,
                    css`
                            max-width: 100% !important;
                        `,
                    null,
                    null,
                    null,
                ])}
                > span {
                    font-size: 16px;
                    font-weight: bold;

                    line-height: normal;
                    color: ${getColor("primary.contrast")};
                }
            }
        `,
    },
    promoErrorText: {
        css: css`
            margin-top: 10px;
            font-weight: normal !important;
            background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/icon-error-circle.svg)
                no-repeat top 2.2px left;
            padding-left: 18px;
            background-size: 12px 12px;
            max-width: initial !important;
            margin-top: 4px;
            display: block;
            flex-grow: 1;
            font-size: 14px;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            text-align: left;
            color: ${getColor("text.main")} !important;
        `,
    },
    removedPromoText: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = summaryStyles;

export const CheckoutReviewAndSubmitSummary: FC<Props> = ({
    id,
    customCart,
    cart,
    newCartState,
    applyPromotion,
    shippingCartState,
    shipToState,
    alternateShipAddress,
    loadCart,
    setUpdateCartLoading,
    checkoutErrorMessage,
    promotionState,
    showShippingExpediteMsg, // This property is being used in P21
    loadCartSummary,
    updateCart,
}) => {
    const [deliveryMethod, setDeliveryMethod] = React.useState("primary");
    const [leftSideDivHeight, setLeftSideDivHeight] = React.useState<number>(0);
    const [leftSideMoreoptDivHeight, setleftSideMoreoptDivHeight] = React.useState<number>(0);
    const { showPromotionErrorMessage, promotionErrorMessage, removedPromoCode } = promotionState;
    const summaryContainerRef = React.useRef<HTMLDivElement>(null);
    if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('rightSideInnerDivHeight');
    }

    // This useEffect is being used in P21
    React.useEffect(() => {
        // Clear cart promotions on component unmount.
        return () => {
            if (!showPromotionErrorMessage
                && (
                    customCart?.properties["customerPromocode"]
                    || customCart?.properties["freeShipPromocode"]
                    || customCart?.properties["freeItem"]
                    || customCart?.properties["childCouponDiscnt"]
                    || customCart?.properties["parentPromoInd"]
                    || newCartState?.value?.properties["customerPromocode"]
                    || newCartState?.value?.properties["freeShipPromocode"]
                    || newCartState?.value?.properties["freeItem"]
                    || newCartState?.value?.properties["childCouponDiscnt"]
                    || newCartState?.value?.properties["parentPromoInd"])
            ) {
                clearPromotion();
            }
        }
    }, []);

    React.useEffect(() => {
        const getHeight = () => {
            const leftSideInnerDivHeight = sessionStorage.getItem('leftSideInnerDivHeight')
            setLeftSideDivHeight(Number(leftSideInnerDivHeight));
        };
        const handleStorageChange = (event: Event) => {
            if (event.type === "click") {
                getHeight();
            } else {
                const parentDiv = document.getElementById('leftSideDiv');
                if (parentDiv && parentDiv.contains(event.target as Node)) {
                    getHeight();
                }
            }
        }
        const intervalId = setInterval(() => {
            const leftSideInnerDivHeight = sessionStorage.getItem("leftSideInnerDivHeight");
            const leftSideMoreoptInnerDivHeight = sessionStorage.getItem("leftSideMoreoptDivHeight");

            if (leftSideInnerDivHeight && parseInt(leftSideInnerDivHeight) > 0) {
                if (summaryContainerRef.current && summaryContainerRef.current.offsetHeight + 25 >= (Number(leftSideInnerDivHeight))) {
                    const maxheight = Number(summaryContainerRef.current.offsetHeight + 50);
                    setLeftSideDivHeight(maxheight);
                    sessionStorage.setItem("rightSideInnerDivHeight", String(maxheight));
                } else {
                    setLeftSideDivHeight(Number(leftSideInnerDivHeight));
                }
            }
        }, 200);
        // Below commented code is being used in P21
        //     if (leftSideMoreoptInnerDivHeight) {
        //         setleftSideMoreoptDivHeight(Number(leftSideMoreoptInnerDivHeight));
        //     }
        //     timeoutId = setTimeout(updateHeights, 10);
        // };
        // updateHeights();   
        // return () => clearTimeout(timeoutId);
        // const intervalId = setInterval(getHeight, 1000);
        return () => clearInterval(intervalId);
    }, []);

    

    const clearPromotion = () => {
        const newCart = JSON.parse(JSON.stringify(customCart));
        newCart!.properties["remove"] = "product";
        newCart!.properties["customerPromocode"] = "";
        newCart!.properties["customDiscountAmountDisplay"] = "";
        newCart!.properties["customDiscountMessage"] = "";
        newCart!.properties["customDiscountGrandTotal"] = "";
        newCart!.properties["customDiscountAmount"] = "";
        newCart!.properties["freeItem"] = "";
        newCart!.properties["freeItemDescription"] = "";
        newCart!.properties["freeQuantity"] = "";
        newCart!.properties["freeItemImageUrl"] = "";
        newCart!.properties["freeItemTotal"] = "";
        newCart!.properties["salePriceItems"] = "";
        newCart!.properties["childCouponDiscnt"] = "";
        newCart!.properties["isParentPromoList"] = ""; // P21 change  
        newCart!.properties["orderSubTotalDisplayForparentPromo"] = "";   // P21 change    
        newCart!.properties["parentSaleItemPrice"] = "";
        newCart!.properties["parentChildList"] = "";
        newCart!.properties["parentPromoInd"] = "";
        newCart!.properties["parordSeq"] = "";
        newCart!.cartLines!.forEach((cartLine : any) => {
            cartLine.pricing.promoSaving = '';
            cartLine.pricing.promoSalePrice = '';
            cartLine.properties["parentChildPromoCode"] = "";
            cartLine.properties["parentChildPromoDiscnt"] = "";
        });
       
        
                  setUpdateCartLoading({
                        loading: true,
                    });
         applyPromotion({
            promotionCode: "",
            onComplete() {
                checkoutErrorMessage({ errorMessage: true });
                loadCartSummary({
            cart: { ...newCart } as unknown as CartModel,
            onSuccess(result: CartSummaryMethodsResults) {
                newCart.properties["orderMaterialTax"] = parseFloat(result?.cartSummary?.salesTax).toString();
                newCart.properties["freightTax"] = parseFloat(result?.cartSummary?.taxOnFreight).toString();
                if (newCart.properties["shippingSelection"] == "pickup" || newCart.properties["shippingSelection"] == "pickup_mob") {
                     newCart.properties["BranchPickupMaterialTax"] = result?.cartSummary.salesTax;
                       }   
                const updatedCart = updateCartCalculationWithNewCart(newCart, "ClearPromo");
                 updateCart({
                    cart: updatedCart,
                    onSuccess(result: ADIUpdateCartResult) {
                    setUpdateCartLoading({
                        loading: false,
                    });
                }
                });
            },
        });
},
});       
    }

    //         apiparameter: {
    //             accountLTLAllowed: cart?.accountLTLAllowed,
    //             branchID: cart?.p21BranchCode,
    //             cannotShipAir: cannotShipAir,
    //             carrierCodes: cart?.carrierCodes,
    //             destinationCity: deliveryMethod === "alternate"
    //                 ? alternateShipAddress?.city || ""
    //                 : shipToState.value?.city || "",
    //             destinationCountry: deliveryMethod === "alternate"
    //                 ? alternateShipAddress?.country?.abbreviation || ""
    //                 : shipToState.value?.country?.abbreviation || "",
    //             destinationState: deliveryMethod === "alternate"
    //                 ? alternateShipAddress?.state?.abbreviation || ""
    //                 : shipToState.value?.state?.abbreviation || "",
    //             destinationZipCode: deliveryMethod === "alternate"
    //                 ? alternateShipAddress?.postalCode || ""
    //                 : shipToState.value?.postalCode || "",
    //             freightOptions: cart?.freightOptions,
    //             hasBulkItem: hasBulkItem,
    //             mustShipLTL: hasShipLTL,
    //             orderTotal: cart?.orderGrandTotal || 0,
    //             orderWeight: shippingWeights,
    //             overrideOriginCity: cart?.overrideOriginCity,
    //             overrideOriginCountry: cart?.overrideOriginCountry,
    //             overrideOriginState: cart?.overrideOriginState,
    //             overrideOriginZipCode: cart?.overrideOriginZipCode,
    //             overrideShipperID: cart?.overrideShipperID,
    //             defaultCarrierCd: cart?.defaultCarrier,
    //             getAllCarriers: false,
    //         }
    //     })
    //     /*************** **/
    // };


    const clearFreeShipPromotion = () => {
        const newCart = JSON.parse(JSON.stringify(customCart));
        const promotion = {
            promotionCode: "",
            properties: {
                remove: "freeship",
            },
        };
        newCart!.properties["remove"] = "freeship";
        newCart!.properties["returnCode"] = "";
        newCart!.properties["freeShipPromocode"] = "";
        newCart!.properties["freeShipDiscountAmountDisplay"] = "";
        newCart!.properties["freeShipDiscountMessage"] = "";
        newCart!.properties["FreeShipDiscountAmount"] = "";
      
        
         setUpdateCartLoading({
                        loading: true,
         });
        
        applyPromotion({
            promotionCode: "",
            onComplete() {
                checkoutErrorMessage({ errorMessage: true });
                  setUpdateCartLoading({
                        loading: true,
                    });
                loadCartSummary({
            cart: { ...newCart } as unknown as CartModel,
            onSuccess(result: CartSummaryMethodsResults) {
                newCart.properties["orderMaterialTax"] = parseFloat(result?.cartSummary?.salesTax).toString();
                newCart.properties["freightTax"] = parseFloat(result?.cartSummary?.taxOnFreight).toString();
                if (newCart.properties["shippingSelection"] == "pickup" || newCart.properties["shippingSelection"] == "pickup_mob") {
                     newCart.properties["BranchPickupMaterialTax"] = result?.cartSummary.salesTax;
                       }   
                const updatedCart = updateCartCalculationWithNewCart(newCart, "ClearPromo");
                  updateCart({
                    cart: updatedCart,
                    onSuccess(result: ADIUpdateCartResult) {
                    setUpdateCartLoading({
                        loading: false,
                    });
                }
                });
            },
        });
},
});       
    };



    return (
        <>
            <StyledWrapper>
                <StyledWrapper {...styles.summaryContainer} id="div2" style={{ height: `${leftSideDivHeight ? leftSideDivHeight : ''}px`, marginTop: `${leftSideMoreoptDivHeight == 0 ? 0 : -leftSideMoreoptDivHeight}px` }}>
                    <StyledWrapper ref={summaryContainerRef}>
                        <StyledWrapper as="h3" {...styles.summaryHeader}>
                            {translate("Summary")}
                        </StyledWrapper>
                        <Zone contentId={id} zoneName="Content"></Zone>
                        {showShippingExpediteMsg == "false" && ( // P21 change
                            <StyledWrapper {...styles.shippingNote}>
                                {translate("Note: Shipping charges may change if you modify your order after it's placed.")}
                            </StyledWrapper>
                        )}

                        {/* Below note is being used in P21  */}
                        {showShippingExpediteMsg == "true" && (
                            <StyledWrapper {...styles.shippingNote}>
                                {translate("Note: Shipping costs are estimates. Final charges may vary and will be confirmed on your invoice.")}
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.helpTextContainer}>
                            {translate("Need Help?")}
                            <br></br>
                            {translate("If we can assist you in any way during your checkout process please contact us.")}
                            <br></br>
                            {translate("We are here to help. Website Support: 1.877.228.6739")}
                        </StyledWrapper>
                        {(customCart?.properties["customerPromocode"]
                            && customCart?.type != 'Quote'
                            && customCart?.properties["customerPromocode"] != " ") && (
                                <Hidden above="sm">
                                    <StyledWrapper {...styles.codeAppliedMobile}>
                                        <StyledWrapper {...styles.codeAppliedText}>
                                            Code Applied: {newCartState.value?.properties.customerPromocode}
                                        </StyledWrapper>
                                        <Link onClick={() => clearPromotion()}>{translate("Remove")}</Link>
                                    </StyledWrapper>
                                </Hidden>
                            )}
                        {(customCart?.properties["freeShipPromocode"]
                            && customCart?.properties["freeShipPromocode"] != " "
                            && customCart?.type != 'Quote') && (
                                <Hidden above="sm">
                                    <StyledWrapper {...styles.codeAppliedMobile}>
                                        <StyledWrapper {...styles.codeAppliedText}>
                                            Code Applied: {newCartState.value?.properties.freeShipPromocode}
                                        </StyledWrapper>
                                        <Link onClick={() => clearFreeShipPromotion()}>{translate("Remove")}</Link>
                                    </StyledWrapper>
                                </Hidden>
                            )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.placeOrderButtonContainer}>
                <StyledWrapper>
                    {(customCart?.properties["customerPromocode"]) && (
                        <Hidden below="md">
                            <StyledWrapper {...styles.codeApplied}>
                                <StyledWrapper {...styles.codeAppliedText}>
                                    Code Applied: {newCartState.value?.properties.customerPromocode}

                                </StyledWrapper>
                                <Link onClick={() => clearPromotion()}>{translate("Remove")}</Link>
                            </StyledWrapper>
                        </Hidden>
                    )}
                    {(customCart?.properties["freeShipPromocode"]) && (
                        <Hidden below="md">
                            <StyledWrapper {...styles.codeApplied}>
                                <StyledWrapper {...styles.codeAppliedText}>
                                    Code Applied: {newCartState.value?.properties.freeShipPromocode}
                                </StyledWrapper>
                                <Link onClick={() => clearFreeShipPromotion()}>{translate("Remove")}</Link>
                            </StyledWrapper>
                        </Hidden>
                    )}
                    {showPromotionErrorMessage &&
                        (
                            <>

                                <StyledWrapper {...styles.promoErrorText}>
                                    <Typography as="p">{promotionErrorMessage} </Typography>
                                    <Typography as="p" {...styles.removedPromoText}>{translate(`Code ${removedPromoCode} has been removed.`)}</Typography>

                                </StyledWrapper>
                            </>
                        )}
                </StyledWrapper>
                {
                    customCart &&
                        !newCartState?.isLoading &&
                        (
                            (!isNaN(Number(customCart?.properties?.finalGrandTotal)) && Number(customCart?.properties?.finalGrandTotal) > 0) ||
                            (customCart?.orderGrandTotal)
                        )
                        ? <PlaceOrderButton />
                        : null
                }
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitSummary),
    definition: {
        group: "Checkout - Review & Submit",
        icon: "PageTitle",
    },
};

export default widgetModule;
